import { useEffect, useState } from "react";
import { apiFetch } from "../../api";
import { BASE_URL as u } from "../../../commonUrls";
import { getAllCategories } from "../categories/CategoryService";

const SUGGEST_URL = `${u}/transaction/categorize-suggestions`;
const APPLY_URL   = `${u}/transaction/apply-categories`;

const SOURCE_LABEL = { MERCHANT_MATCH: "Matched", RULE: "Rule", AI: "AI", NONE: "" };
const SOURCE_COLOR = { MERCHANT_MATCH: "#22c55e", RULE: "#f59e0b", AI: "#8b5cf6", NONE: "#6b7280" };
const CONF_COLOR   = { HIGH: "#22c55e", MEDIUM: "#f59e0b", LOW: "#f87171" };

export default function CategorizationModal({ onClose }) {
  const [loading, setLoading]       = useState(true);
  const [applying, setApplying]     = useState(false);
  const [data, setData]             = useState(null);
  const [categories, setCategories] = useState([]);
  // overrides: { [transactionId]: { categoryId, merchantId, merchantName, needsReview } }
  const [overrides, setOverrides]   = useState({});
  // flagged: Set of transactionIds the user has marked "remind me later"
  const [flagged, setFlagged]       = useState(new Set());
  const [filter, setFilter]         = useState("all"); // all | suggested | flagged | unseen
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState(null);

  useEffect(() => {
    Promise.all([fetchSuggestions(), fetchCategories()]);
  }, []);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await apiFetch(SUGGEST_URL);
      const json = await res.json();
      setData(json);

      // Auto-seed confirmed overrides from HIGH/MEDIUM confidence suggestions
      const initial = {};
      const preFlagged = new Set();
      (json.rows || []).forEach(row => {
        if (row.needsReview) {
          preFlagged.add(row.transactionId);
        } else if (row.suggestedCategoryId &&
            (row.enrichmentConfidence === "HIGH" || row.enrichmentConfidence === "MEDIUM")) {
          initial[row.transactionId] = {
            categoryId:   row.suggestedCategoryId,
            merchantId:   null,
            merchantName: row.merchantHint || null,
            needsReview:  false,
          };
        }
      });
      setOverrides(initial);
      setFlagged(preFlagged);
      // Always start on "all" — flagged tab disables dropdowns which confuses users
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await getAllCategories();
      if (Array.isArray(data)) setCategories(data);
    } catch (_) {}
  };

  // ── Row-level state setters ─────────────────────────────────
  const setRowCategory = (txId, categoryId) => {
    setFlagged(prev => { const next = new Set(prev); next.delete(txId); return next; });
    setOverrides(prev => ({
      ...prev,
      [txId]: { ...(prev[txId] || {}), categoryId: categoryId || null, needsReview: false }
    }));
  };

  const setRowMerchant = (txId, merchantName) => {
    setOverrides(prev => ({
      ...prev,
      [txId]: { ...(prev[txId] || {}), merchantId: null, merchantName }
    }));
  };

  const toggleFlag = (txId) => {
    setFlagged(prev => {
      const next = new Set(prev);
      if (next.has(txId)) {
        next.delete(txId);
      } else {
        next.add(txId);
        // Remove any confirmed category if user flags it (they're deferring)
        setOverrides(o => {
          const updated = { ...o };
          delete updated[txId];
          return updated;
        });
      }
      return next;
    });
  };

  // ── Bulk actions ─────────────────────────────────────────────
  const acceptAll = () => {
    const next = { ...overrides };
    (data?.rows || []).forEach(row => {
      if (row.suggestedCategoryId && !flagged.has(row.transactionId)) {
        next[row.transactionId] = {
          categoryId:   row.suggestedCategoryId,
          merchantId:   null,
          merchantName: row.merchantHint || null,
          needsReview:  false,
        };
      }
    });
    setOverrides(next);
  };

  const clearAll = () => {
    setOverrides({});
    setFlagged(new Set());
  };

  // ── Save ─────────────────────────────────────────────────────
  const handleApply = async () => {
    const payload = {};
    // Confirmed rows
    Object.entries(overrides).forEach(([txId, ov]) => {
      if (ov.categoryId || ov.merchantId || ov.merchantName) {
        payload[txId] = { ...ov, needsReview: false };
      }
    });
    // Flagged rows
    flagged.forEach(txId => {
      payload[txId] = { categoryId: null, merchantId: null, merchantName: null, needsReview: true };
    });

    if (Object.keys(payload).length === 0) return;
    setApplying(true);
    try {
      const res  = await apiFetch(APPLY_URL, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      const json = await res.json();
      setResult({ applied: json.applied ?? Object.keys(payload).length, flagged: flagged.size });
    } catch (e) {
      setError(e.message);
    } finally {
      setApplying(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────
  const rows           = data?.rows ?? [];
  const confirmedCount = Object.values(overrides).filter(o => o.categoryId).length;
  const actionCount    = confirmedCount + flagged.size;

  const filteredRows = rows.filter(row => {
    if (filter === "suggested") return !!row.suggestedCategoryId && !row.needsReview;
    if (filter === "flagged")   return flagged.has(row.transactionId) || row.needsReview;
    if (filter === "unseen")    return !row.suggestedCategoryId && !row.needsReview && !flagged.has(row.transactionId);
    return true;
  });

  // ── Result screen ─────────────────────────────────────────────
  if (result != null) {
    return (
      <div style={overlay} onClick={onClose}>
        <div style={modalBox} onClick={e => e.stopPropagation()}>
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
            <h3 style={{ margin: "0 0 8px" }}>Saved</h3>
            <div style={{ color: "var(--text-muted)", marginBottom: 24, fontSize: 14 }}>
              {result.applied - result.flagged > 0 && (
                <div>{result.applied - result.flagged} transaction{result.applied - result.flagged !== 1 ? "s" : ""} categorized.</div>
              )}
              {result.flagged > 0 && (
                <div style={{ color: "#f59e0b", marginTop: 4 }}>
                  {result.flagged} flagged for later — they'll appear at the top next time.
                </div>
              )}
            </div>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            <h3 style={{ margin: 0 }}>Categorize Transactions</h3>
            {data && (
              <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
                {data.totalUncategorized} uncategorized
                {data.flaggedCount > 0 && <span style={{ color: "#f59e0b" }}> · {data.flaggedCount} flagged from last time</span>}
                {data.autoSuggested > 0 && <span style={{ color: "#8b5cf6" }}> · {data.autoSuggested} AI suggestions</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {error && (
          <div style={{ color: "#f87171", background: "rgba(239,68,68,0.08)", border: "1px solid #ef4444",
            borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "48px 0", color: "var(--text-muted)" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
            Analysing transactions with AI…
          </div>
        )}

        {!loading && data && (
          <>
            {/* Stats */}
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 14,
              padding: "10px 14px", background: "rgba(255,255,255,0.04)", borderRadius: 8 }}>
              <Stat label="Uncategorized" value={data.totalUncategorized} color="#6b7280" />
              <Stat label="AI suggested"  value={data.autoSuggested}      color="#8b5cf6" />
              <Stat label="Confirmed"     value={confirmedCount}           color="#22c55e" />
              <Stat label="Flag later"    value={flagged.size}             color="#f59e0b" />
            </div>

            {/* Toolbar */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { key: "all",       label: "All" },
                  { key: "suggested", label: "Has suggestion" },
                  { key: "flagged",   label: `Flagged${flagged.size ? ` (${flagged.size})` : ""}` },
                  { key: "unseen",    label: "Needs review" },
                ].map(f => (
                  <button key={f.key} onClick={() => setFilter(f.key)}
                    style={{ ...filterBtn, ...(filter === f.key ? filterBtnActive : {}),
                      ...(f.key === "flagged" && flagged.size > 0 ? { borderColor: "#f59e0b", color: "#fbbf24" } : {}) }}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn" style={{ fontSize: 11, padding: "4px 10px" }} onClick={acceptAll}>Accept all AI</button>
                <button className="btn" style={{ fontSize: 11, padding: "4px 10px" }} onClick={clearAll}>Clear all</button>
              </div>
            </div>

            {/* Flag-later hint */}
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10, paddingLeft: 2 }}>
              Tip: click 🔖 on any row to flag it for later — it'll reappear at the top next time you open this.
            </div>

            {filteredRows.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: "var(--text-muted)" }}>
                No transactions in this filter.
              </div>
            ) : (
              <div style={{ maxHeight: 420, overflowY: "auto", borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.08)" }}>
                {filteredRows.map((row, i) => {
                  const ov         = overrides[row.transactionId] || {};
                  const catId      = ov.categoryId !== undefined ? ov.categoryId : row.suggestedCategoryId;
                  const merchant   = ov.merchantName !== undefined ? ov.merchantName : (row.merchantHint || "");
                  const isFlagged  = flagged.has(row.transactionId);
                  const isConfirmed = !!catId && !isFlagged;
                  const wasFlgged  = row.needsReview; // flagged in a previous session

                  return (
                    <div key={row.transactionId} style={{
                      display: "grid", gridTemplateColumns: "1fr auto", gap: 12,
                      padding: "10px 14px",
                      borderBottom: i < filteredRows.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                      background: isFlagged
                        ? "rgba(245,158,11,0.05)"
                        : isConfirmed
                          ? "rgba(34,197,94,0.03)"
                          : "transparent",
                      borderLeft: isFlagged ? "3px solid #f59e0b"
                        : wasFlgged && !isFlagged ? "3px solid rgba(245,158,11,0.3)"
                        : "3px solid transparent",
                    }}>
                      {/* Left */}
                      <div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{row.date}</span>
                          <span style={{ fontSize: 13 }} title={row.description}>{truncate(row.description, 60)}</span>
                          <span style={{ fontWeight: 700, whiteSpace: "nowrap", fontSize: 13,
                            color: row.type === "INCOME" ? "#22c55e" : "#f87171" }}>
                            {row.type === "INCOME" ? "+" : "−"}₹{Number(row.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                          <MerchantInput value={merchant} onChange={v => setRowMerchant(row.transactionId, v)} disabled={isFlagged} />
                          {row.enrichmentSource && row.enrichmentSource !== "NONE" && (
                            <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, flexShrink: 0,
                              color: SOURCE_COLOR[row.enrichmentSource], background: SOURCE_COLOR[row.enrichmentSource] + "22" }}>
                              {SOURCE_LABEL[row.enrichmentSource]}
                            </span>
                          )}
                          {isFlagged && (
                            <span style={{ fontSize: 11, color: "#f59e0b", fontStyle: "italic" }}>flagged for later</span>
                          )}
                          {wasFlgged && !isFlagged && (
                            <span style={{ fontSize: 11, color: "rgba(245,158,11,0.5)" }}>was flagged</span>
                          )}
                        </div>
                      </div>

                      {/* Right: flag + category */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        {/* Flag button */}
                        <button
                          onClick={() => toggleFlag(row.transactionId)}
                          title={isFlagged ? "Remove flag" : "Flag for later"}
                          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: "2px 4px",
                            opacity: isFlagged ? 1 : 0.35, color: "#f59e0b", lineHeight: 1,
                            transition: "opacity 0.15s" }}>
                          🔖
                        </button>

                        {/* Confirm tick */}
                        {isConfirmed && <span style={{ fontSize: 14, color: "#22c55e" }}>✓</span>}

                        {/* Category dropdown */}
                        <select
                          value={isFlagged ? "" : (catId || "")}
                          onChange={e => setRowCategory(row.transactionId, e.target.value)}
                          disabled={isFlagged}
                          style={{
                            fontSize: 12, padding: "4px 6px", borderRadius: 6, minWidth: 150,
                            border: isFlagged
                              ? "1px solid rgba(245,158,11,0.3)"
                              : catId
                                ? "1px solid rgba(34,197,94,0.4)"
                                : "1px solid rgba(255,255,255,0.12)",
                            background: "var(--surface, #1e1e2e)",
                            color: isFlagged ? "#6b7280" : catId ? "inherit" : "#6b7280",
                            cursor: isFlagged ? "not-allowed" : "pointer",
                          }}>
                          <option value="">{isFlagged ? "— Flagged —" : "— Set category —"}</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.categoryName}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              paddingTop: 14, marginTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ fontSize: 13 }}>
                {actionCount === 0
                  ? <span style={{ color: "var(--text-muted)" }}>Use the dropdowns above to set categories, or 🔖 to flag for later.</span>
                  : <span>
                      {confirmedCount > 0 && <span style={{ color: "#22c55e", fontWeight: 600 }}>{confirmedCount} categorized</span>}
                      {confirmedCount > 0 && flagged.size > 0 && <span style={{ color: "var(--text-muted)" }}> · </span>}
                      {flagged.size > 0 && <span style={{ color: "#f59e0b", fontWeight: 600 }}>{flagged.size} flagged for later</span>}
                    </span>}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button className="btn" onClick={onClose}>Cancel</button>
                {actionCount > 0
                  ? <button className="btn btn-primary" onClick={handleApply} disabled={applying}
                      style={{ background: "#6366f1", border: "none", color: "#fff",
                        padding: "8px 20px", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
                      {applying ? "Saving…" : `Save (${actionCount})`}
                    </button>
                  : <button className="btn" disabled
                      style={{ opacity: 0.35, cursor: "not-allowed", padding: "8px 20px" }}>
                      Save
                    </button>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Inline merchant name editor ───────────────────────────────
function MerchantInput({ value, onChange, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  const commit = () => { setEditing(false); onChange(draft); };

  if (disabled) {
    return (
      <span style={{ fontSize: 11, color: "#4b5563", maxWidth: 150, overflow: "hidden",
        textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value || "—"}
      </span>
    );
  }

  if (editing) {
    return (
      <input value={draft} onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        style={{ fontSize: 11, padding: "1px 4px", borderRadius: 4, border: "1px solid #6366f1",
          background: "transparent", color: "inherit", width: 130 }}
        autoFocus />
    );
  }
  return (
    <span onClick={() => setEditing(true)} title="Click to edit merchant name"
      style={{ fontSize: 11, cursor: "pointer", borderBottom: "1px dashed #4b5563",
        color: value ? "#94a3b8" : "#4b5563", whiteSpace: "nowrap",
        overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 }}>
      {value || "Set merchant…"}
    </span>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: "center", minWidth: 60 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{label}</div>
    </div>
  );
}

function truncate(s, max) {
  if (!s) return "";
  return s.length <= max ? s : s.substring(0, max) + "…";
}

const overlay  = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalBox = { background: "var(--surface, #1e1e2e)",
  border: "1px solid var(--border, rgba(255,255,255,0.12))",
  borderRadius: 12, padding: 24, maxWidth: 860, width: "96vw", maxHeight: "93vh", overflowY: "auto" };
const closeBtn = { background: "none", border: "none", fontSize: 22, cursor: "pointer",
  color: "var(--text-muted)", lineHeight: 1 };
const filterBtn = { fontSize: 11, padding: "4px 10px", borderRadius: 6, cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.12)", background: "transparent", color: "var(--text-muted)" };
const filterBtnActive = { borderColor: "#6366f1", color: "#818cf8", background: "rgba(99,102,241,0.12)" };
